import React, { useState } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ADMIN_BADGE_ICON_SIZE, resolveBadgeIconSrc } from './badgeIconUtils';

const LIGHTBOX_IMAGE_SIZE = 320;

type BadgeIconLightboxProps = {
  open: boolean;
  src: string | null;
  title?: string;
  onClose: () => void;
};

export const BadgeIconLightbox: React.FC<BadgeIconLightboxProps> = ({
  open,
  src,
  title,
  onClose,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={false}
    slotProps={{
      backdrop: {
        sx: { bgcolor: 'rgba(0, 0, 0, 0.88)' },
      },
    }}
    PaperProps={{
      sx: {
        bgcolor: 'transparent',
        boxShadow: 'none',
        overflow: 'visible',
        m: 2,
        maxWidth: 'none',
      },
    }}
  >
    <IconButton
      onClick={onClose}
      aria-label="Close preview"
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        color: 'common.white',
        bgcolor: 'rgba(0,0,0,0.35)',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
      }}
    >
      <CloseIcon />
    </IconButton>

    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        outline: 'none',
      }}
    >
      {title ? (
        <Typography
          variant="h6"
          sx={{ color: 'common.white', textAlign: 'center', fontWeight: 600 }}
        >
          {title}
        </Typography>
      ) : null}
      {src ? (
        <Box
          component="img"
          src={src}
          alt={title || 'Badge icon preview'}
          sx={{
            width: LIGHTBOX_IMAGE_SIZE,
            height: LIGHTBOX_IMAGE_SIZE,
            maxWidth: 'min(80vw, 512px)',
            maxHeight: 'min(80vh, 512px)',
            objectFit: 'contain',
            display: 'block',
            bgcolor: 'transparent',
          }}
        />
      ) : null}
    </Box>
  </Dialog>
);

type ClickableBadgeIconProps = {
  icon?: string;
  title?: string;
  size?: number;
  sx?: SxProps<Theme>;
};

export const ClickableBadgeIcon: React.FC<ClickableBadgeIconProps> = ({
  icon,
  title,
  size = ADMIN_BADGE_ICON_SIZE,
  sx,
}) => {
  const [open, setOpen] = useState(false);
  const src = resolveBadgeIconSrc(icon);

  if (!src) {
    return (
      <Typography variant="caption" color="text.secondary">
        default
      </Typography>
    );
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        aria-label={title ? `Preview badge icon: ${title}` : 'Preview badge icon'}
        sx={{
          p: 0,
          border: 'none',
          bgcolor: 'transparent',
          cursor: 'zoom-in',
          borderRadius: 1,
          lineHeight: 0,
          transition: 'transform 0.15s ease, opacity 0.15s ease',
          '&:hover': {
            transform: 'scale(1.06)',
            opacity: 0.92,
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
          ...sx,
        }}
      >
        <Box
          component="img"
          src={src}
          alt=""
          sx={{
            width: size,
            height: size,
            objectFit: 'contain',
            objectPosition: 'center',
            display: 'block',
            bgcolor: 'transparent',
            pointerEvents: 'none',
          }}
        />
      </Box>
      <BadgeIconLightbox
        open={open}
        src={src}
        title={title}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
